// Vendor-free PII/secret scrubber for error-tracking events.
//
// Deliberately imports no SDK: it is a set of pure functions over minimal
// structural shapes, so it stays testable in the plain-node vitest environment
// and clean of the @sentry import fence (eslint.config.mjs). Sentry's
// `beforeSend`/`beforeBreadcrumb` hooks are structurally compatible with these
// shapes; the config files cast at the seam.
//
// Contract: never let an error report carry who the user is or any credential.
// We drop the whole request body/cookies/query and every credential-bearing
// header (the Better Auth session cookie above all); and we strip bearer tokens
// out of every URL the SDK captures — request.url and the navigation/fetch/xhr
// breadcrumb trail — because two live routes put tokens in the URL:
// reset-password (`?token=` in the query) and accept-invitation (the token as a
// path segment). Server-side data-scrubbing at the vendor is not relied upon.

export type ScrubbableEvent = {
  request?: {
    url?: string;
    headers?: Record<string, string>;
    cookies?: unknown;
    data?: unknown;
    query_string?: unknown;
    [k: string]: unknown;
  };
  user?: unknown;
  [k: string]: unknown;
};

export type ScrubbableBreadcrumb = {
  category?: string;
  message?: string;
  data?: {
    url?: unknown;
    to?: unknown;
    from?: unknown;
    [k: string]: unknown;
  };
  [k: string]: unknown;
};

// Matched as case-insensitive substrings so header-name variants can't slip
// through: "authorization"/"Proxy-Authorization", "Cookie"/"Set-Cookie",
// "X-Api-Key"/"api_key"/"apikey", and any "*-token" (CSRF, bearer, session).
const SENSITIVE_HEADER_SUBSTRINGS = [
  "authorization",
  "cookie",
  "api-key",
  "api_key",
  "apikey",
  "token",
] as const;

function isSensitiveHeaderName(name: string): boolean {
  const lower = name.toLowerCase();
  return SENSITIVE_HEADER_SUBSTRINGS.some((needle) => lower.includes(needle));
}

// Routes that carry a bearer token as a PATH segment; the segment immediately
// after the prefix is redacted. (Query-borne tokens like reset-password's
// `?token=` are removed wholesale when sanitizeUrl drops the query string.)
const TOKEN_IN_PATH_REDACTIONS: { re: RegExp; replacement: string }[] = [
  { re: /(\/accept-invitation\/)[^/?#]+/gi, replacement: "$1[redacted]" },
];

function redactTokenPathSegments(path: string): string {
  let out = path;
  for (const { re, replacement } of TOKEN_IN_PATH_REDACTIONS) {
    out = out.replace(re, replacement);
  }
  return out;
}

/**
 * Reduce a URL to a token-free form: drop the query string and fragment, and
 * redact bearer tokens carried as a path segment (e.g. /accept-invitation/<t>).
 * Pure and defensive — never throws; returns a best-effort stripped string, or
 * the original input if it cannot be processed.
 */
export function sanitizeUrl(url: string): string {
  if (typeof url !== "string" || url === "") return url;
  try {
    // Drop everything from the first `?` or `#` — the reset-password token lives
    // in the query. Works for absolute URLs and bare paths alike.
    const cutIdx = url.search(/[?#]/);
    const base = cutIdx === -1 ? url : url.slice(0, cutIdx);
    return redactTokenPathSegments(base);
  } catch {
    return url;
  }
}

/**
 * Strip identity and credentials from an outbound error-tracking event.
 * Mutates and returns the same reference (Sentry's `beforeSend` contract).
 */
export function beforeSend<E extends ScrubbableEvent>(event: E): E {
  // Identity never ships with a crash report.
  delete event.user;

  const request = event.request;
  if (request) {
    // Whole-body carriers of secrets/PII — dropped outright.
    delete request.cookies;
    delete request.data;
    delete request.query_string;

    // The request URL can carry a token in its query/path — strip both.
    if (typeof request.url === "string") {
      request.url = sanitizeUrl(request.url);
    }

    const headers = request.headers;
    if (headers && typeof headers === "object") {
      // Rebuild without the credential-bearing headers rather than deleting by
      // computed key (keeps the lint clean; same effect).
      const cleaned: Record<string, string> = {};
      for (const [name, value] of Object.entries(headers)) {
        if (!isSensitiveHeaderName(name)) {
          cleaned[name] = value;
        }
      }
      request.headers = cleaned;
    }
  }

  return event;
}

/**
 * Sanitize URLs carried by navigation/fetch/xhr breadcrumbs before the trail
 * leaves the process. The SDK keeps up to 100 crumbs with full URLs, so a token
 * on any visited route would leak here even when beforeSend is clean. Mutates
 * and returns the same reference (Sentry's `beforeBreadcrumb` contract).
 */
export function beforeBreadcrumb<B extends ScrubbableBreadcrumb>(breadcrumb: B): B {
  const data = breadcrumb.data;
  if (data) {
    if (typeof data.url === "string") data.url = sanitizeUrl(data.url);
    if (typeof data.to === "string") data.to = sanitizeUrl(data.to);
    if (typeof data.from === "string") data.from = sanitizeUrl(data.from);
  }
  // Navigation crumbs sometimes hold the URL in the message field.
  if (breadcrumb.category === "navigation" && typeof breadcrumb.message === "string") {
    breadcrumb.message = sanitizeUrl(breadcrumb.message);
  }
  return breadcrumb;
}
